import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';
import { ILayoutProps } from './BaseLayout';
import { detailLeftAnimations, getAnimationProps } from './animations';

const DetailLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    eventBrandLogo,
    logoAltText,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'md' as const }) ?? 'sm';
    const showLogo = eventBrandLogo;
    const studioLogoSize = useBreakpointValue({ base: 'md' as const, lg: 'lg' as const });

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            <Box position="relative" width="full" height="full" overflow="hidden">
                {/* Cover image */}
                <motion.div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%'
                    }}
                    {...getAnimationProps(detailLeftAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Single responsive translucent panel on the left */}
                <motion.div
                    style={{
                        position: 'absolute',
                        height: '100%',
                        width: useBreakpointValue({ base: '75%', lg: '25%' }) || '25%',
                        left: 0,
                        top: 0,
                        background:
                            colorScheme === 'dark'
                                ? theme.colors.blackAlpha[700]
                                : theme.colors.whiteAlpha[700],
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding:
                            useBreakpointValue({ base: '24px 16px', lg: '24px 32px' }) ||
                            '24px 32px'
                    }}
                    {...getAnimationProps(detailLeftAnimations.coverImage, enableAnimations)}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '32px',
                            left: 0,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        {showLogo ? (
                            <motion.div
                                {...getAnimationProps(
                                    detailLeftAnimations.galleryName,
                                    enableAnimations
                                )}
                            >
                                <StudioLogo
                                    logo={eventBrandLogo}
                                    logoAltText={logoAltText}
                                    logoSize={studioLogoSize}
                                />
                            </motion.div>
                        ) : null}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            gap: '32px',
                            zIndex: 1
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                gap: '16px'
                            }}
                        >
                            <motion.div
                                {...getAnimationProps(
                                    detailLeftAnimations.galleryName,
                                    enableAnimations
                                )}
                            >
                                <GalleryHeading
                                    size={galleryHeadingSize}
                                    text={galleryTitle}
                                    variant={colorScheme === 'dark' ? 'light' : 'dark'}
                                />
                            </motion.div>
                            {showLogo ? null : (
                                <>
                                    <motion.div
                                        {...getAnimationProps(
                                            detailLeftAnimations.line!,
                                            enableAnimations
                                        )}
                                    >
                                        <div
                                            style={{
                                                width: '140px',
                                                height: '1px',
                                                backgroundColor:
                                                    colorScheme === 'dark' ? 'white' : 'black',
                                                margin: '0 auto',
                                                display: 'block'
                                            }}
                                        />
                                    </motion.div>
                                    <motion.div
                                        {...getAnimationProps(
                                            detailLeftAnimations.studioName,
                                            enableAnimations
                                        )}
                                    >
                                        <StudioHeading
                                            text={brandName}
                                            variant={colorScheme === 'dark' ? 'light' : 'dark'}
                                        />
                                    </motion.div>
                                </>
                            )}
                        </div>
                        <div
                            style={{
                                alignSelf:
                                    useBreakpointValue({ base: 'stretch', lg: 'center' }) ||
                                    'center'
                            }}
                        >
                            <motion.div
                                {...getAnimationProps(
                                    detailLeftAnimations.viewBtn,
                                    enableAnimations
                                )}
                            >
                                <Button
                                    paddingX={12}
                                    text={buttonText}
                                    textLarge={buttonTextLarge}
                                    variant={
                                        `secondary ${
                                            colorScheme === 'dark' ? 'light' : 'dark'
                                        }` as const
                                    }
                                    fullWidth={
                                        useBreakpointValue({ base: true, lg: false }) || false
                                    }
                                />
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </Box>
        </Box>
    );
};

export default DetailLayout;
